import { Box, Typography, Tooltip } from '@mui/material';
import { AccessTime, Warning, Error } from '@mui/icons-material';
import { formatDueDate, getDueDateStatus, formatAnchorDateLabel } from './checklist-utils';
import { ChecklistDueAnchor } from '@inversor-hub/types';
import dayjs from 'dayjs';

interface TaskDueDateBadgeProps {
  dueAt?: string;
  dueAnchor?: ChecklistDueAnchor;
  dueOffsetDays?: number;
}

const TaskDueDateBadge = ({ dueAt, dueAnchor, dueOffsetDays }: TaskDueDateBadgeProps) => {
  if (!dueAt) {
    return null;
  }

  const status = getDueDateStatus(dueAt);
  const formattedDate = formatDueDate(dueAt);
  const anchorLabel = formatAnchorDateLabel(dueAnchor, dueOffsetDays);
  const daysUntil = dayjs(dueAt).diff(dayjs(), 'day');

  const getColor = () => {
    switch (status) {
      case 'overdue':
        return 'error.main';
      case 'soon':
        return 'warning.main';
      case 'ok':
        return 'info.main';
      default:
        return 'text.secondary';
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'overdue':
        return <Error fontSize="small" />;
      case 'soon':
        return <Warning fontSize="small" />;
      default:
        return <AccessTime fontSize="small" />;
    }
  };

  const getLabel = () => {
    if (status === 'overdue') {
      const daysOverdue = Math.abs(daysUntil);
      return `Vencido (${daysOverdue} ${daysOverdue === 1 ? 'día' : 'días'})`;
    }
    if (status === 'soon') {
      if (daysUntil === 0) {
        return 'Vence hoy';
      }
      return `Vence en ${daysUntil} ${daysUntil === 1 ? 'día' : 'días'}`;
    }
    return formattedDate;
  };

  const tooltipTitle = anchorLabel ? `${formattedDate} (${anchorLabel})` : formattedDate;

  return (
    <Tooltip title={tooltipTitle} arrow>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          color: getColor(),
          bgcolor: status === 'overdue' ? 'error.50' : status === 'soon' ? 'warning.50' : 'info.50',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.75rem',
        }}
      >
        {getIcon()}
        <Typography variant="caption" fontWeight={500}>
          {getLabel()}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default TaskDueDateBadge;
